import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useT } from '../i18n';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

/*
  Domains that can be picked: verified ones plus any already in the pool (so a
  domain that lost its verification is not dropped silently). Pool order is
  kept; new domains go to the end.
*/
export function poolDomainChoices(domains, pool) {
  const inPool = new Set((pool && pool.domain_ids) || []);
  return (domains || []).filter((d) => d.status === 'verified' || inPool.has(d.id));
}

export function togglePoolDomain(ids, id, on) {
  if (on) return ids.includes(id) ? ids : [...ids, id];
  return ids.filter((x) => x !== id);
}

/*
  Domains page: "Edit pool" — name, strategy, domains, description
  (PATCH /api/domain-pools/{id}). Editing a pool needs the manager role.
*/
export default function DomainPoolEditDialog({ pool, domains = [], open, onOpenChange, onSaved }) {
  const { t } = useT();
  const [name, setName] = useState('');
  const [strategy, setStrategy] = useState('round_robin');
  const [ids, setIds] = useState([]);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (pool && open) {
      setName(pool.name || '');
      setStrategy(pool.strategy || 'round_robin');
      setIds([...(pool.domain_ids || [])]);
      setDescription(pool.description || '');
    }
  }, [pool, open]);

  const save = async () => {
    if (!name.trim() || !ids.length) return;
    setSaving(true);
    try {
      const r = await axios.patch(API + '/domain-pools/' + pool.id, {
        name: name.trim(),
        strategy,
        domain_ids: ids,
        description,
      });
      toast.success(t('Domain pool saved'));
      if (onSaved) onSaved(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error((e && e.response && e.response.data && e.response.data.detail) || t('Could not save the domain pool'));
    } finally {
      setSaving(false);
    }
  };

  const choices = poolDomainChoices(domains, pool);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('Edit domain pool')}</DialogTitle>
          <DialogDescription>{t('Links already given out keep working; the changes apply to new links.')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="pool-edit-name">{t('Pool name')}</Label>
            <Input id="pool-edit-name" data-testid="pool-edit-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pool-edit-strategy">{t('Pool strategy')}</Label>
            <select
              id="pool-edit-strategy"
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={strategy}
              onChange={(e) => setStrategy(e.target.value)}
            >
              <option value="round_robin">{t('Round robin')}</option>
              <option value="weighted">{t('Weighted')}</option>
              <option value="sticky">{t('Sticky (per-visitor)')}</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label>{t('Pool domains')}</Label>
            <div className="flex flex-wrap gap-2">
              {choices.map((d) => (
                <label key={d.id} className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs">
                  <input
                    type="checkbox"
                    data-testid={'pool-edit-domain-' + d.id}
                    checked={ids.includes(d.id)}
                    onChange={(e) => setIds((prev) => togglePoolDomain(prev, d.id, e.target.checked))}
                  />
                  <span className="font-mono">{d.hostname}</span>
                </label>
              ))}
            </div>
            {!ids.length ? <p className="text-xs text-destructive">{t('Choose at least one domain.')}</p> : null}
          </div>
          <div className="space-y-1">
            <Label htmlFor="pool-edit-description">{t('Pool description')}</Label>
            <Textarea id="pool-edit-description" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('Cancel')}
          </Button>
          <Button onClick={save} disabled={saving || !name.trim() || !ids.length} data-testid="pool-edit-save">
            {t('Save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
